// There is an issue with Intl.DateTimeFormat obsolete implementation that returns wrong value for some of timezones for older browser version
// (e.g. in Firefox version 97, for America/Monterrey it returns GTM-5 instead of GMT-6 in summer time - Mexico dropped DST in 2023)

import type { UrlLang } from '@lingoda/env';

// This polyfill fixes will force the browser to use the up to date implementation from ""@formatjs/intl-datetimeformat" package which is update frequently
export const polyfillTimezones = async (lang: UrlLang) => {
    // get browser timezone to restore after polyfill is applied
    // Since JS Engines do not expose default timezone, there's currently no way for us to detect local timezone after this polyfill is applied
    // https://formatjs.io/docs/polyfills/intl-datetimeformat/#default-timezone
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // As polyfill will apply its own implementation only when no previous exists (by default)
    // we should load a force logic before, otherwise polyfill will be skipped
    await import('@formatjs/intl-datetimeformat/polyfill-force.js');
    await Promise.all([
        import('@formatjs/intl-datetimeformat/add-all-tz.js'),
        mapLocaleToLocaleDataImport(lang),
    ]);

    if ('__setDefaultTimeZone' in Intl.DateTimeFormat) {
        (Intl.DateTimeFormat.__setDefaultTimeZone as (tz: string) => void)(browserTimeZone);
    }
};

const mapLocaleToLocaleDataImport = (locale: UrlLang) => {
    switch (locale) {
        case 'en':
            return import('@formatjs/intl-datetimeformat/locale-data/en.js');
        case 'de':
            return import('@formatjs/intl-datetimeformat/locale-data/de.js');
        case 'ru':
            return import('@formatjs/intl-datetimeformat/locale-data/ru.js');
        case 'fr':
            return import('@formatjs/intl-datetimeformat/locale-data/fr.js');
        case 'es':
            return import('@formatjs/intl-datetimeformat/locale-data/es.js');
        case 'it':
            return import('@formatjs/intl-datetimeformat/locale-data/it.js');
        case 'pl':
            return import('@formatjs/intl-datetimeformat/locale-data/pl.js');
        case 'tr':
            return import('@formatjs/intl-datetimeformat/locale-data/tr.js');
        case 'br':
            return import('@formatjs/intl-datetimeformat/locale-data/pt.js');
        default:
            assertExhaustedSwitch(locale);
            throw new Error(`Unsupported locale: ${locale}`);
    }
};

const assertExhaustedSwitch = (switchCase: never) => {
    console.error(new Error(`Unhandled switch case: ${JSON.stringify(switchCase)}`));
};

export const shouldApplyPolyfillTimezone = (): boolean => {
    return !!CHECK_CASES.find((checkCase) => !isTimeZoneOffsetExpected(checkCase));
};

type CheckCase = { checkDate: string; expectedOffset: string; timeZone: string };

export const isTimeZoneOffsetExpected = ({ checkDate, expectedOffset, timeZone }: CheckCase) => {
    const midnightTime = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        timeZone,
    }).format(new Date(`${checkDate}${expectedOffset}`));

    return midnightTime === '00:00' || midnightTime === '24:00';
};

// Add more checks when errors, like TimeZoneMismatchError, are reported in Sentry.
// When one of those will got unexpected value, polyfill will be applied.
export const CHECK_CASES: CheckCase[] = [
    { checkDate: '2023-09-06T00:00:00', expectedOffset: '-06:00', timeZone: 'America/Monterrey' },
    { checkDate: '2023-09-06T00:00:00', expectedOffset: '+03:00', timeZone: 'Africa/Cairo' },
    { checkDate: '2023-09-06T00:00:00', expectedOffset: '-06:00', timeZone: 'America/Mexico_City' },
    { checkDate: '2025-03-25T00:00:00', expectedOffset: '-03:00', timeZone: 'America/Asuncion' },
    // IANA 2026b/c: Morocco permanent UTC+0, British Columbia permanent -07, Alberta permanent -06
    { checkDate: '2026-10-15T00:00:00', expectedOffset: '+00:00', timeZone: 'Africa/Casablanca' },
    { checkDate: '2026-12-15T00:00:00', expectedOffset: '-07:00', timeZone: 'America/Vancouver' },
    { checkDate: '2026-12-15T00:00:00', expectedOffset: '-06:00', timeZone: 'America/Edmonton' },
];
